<script>
export const group = 'Full'
</script>

<script setup>
import { onUnmounted } from 'vue'
import { colors } from '../../../tailwindTheme.config.js'
import AnimatedBackground from '../../AnimatedBackground.vue'
import ToFullTopLeft from './transitions/energetic_frequencies/ToFullTopLeft.vue'
import PositiveWaveLoop1 from '../../../assets/animations/energetic_frequencies/loops/positive_wave_loop_1.json'
import { frames } from '../../../utils/frames.js'
import gsap from 'gsap'
import CharacterJumpFromTheRight from './text_animations/CharacterJumpFromTheRight.vue'
import CharacterEnlarge from './text_animations/CharacterEnlarge.vue'
import VideoPlayer from '../../VideoPlayer.vue'
import TiktokIcon from '../../../assets/images/tiktok.svg'
const gsapTimeline = useGsapTimeline()

const { item } = defineProps({
  item: Object,
})

const scheme = { backgroundColor: '#fff', color: colors.q.cr.gray.lighter }

const phoneElement = ref(null)
const panesElement = ref(null)
const socialMediaReferencesElement = ref(null)
const referenceImageElements = ref([])

const timelineTitle = ref(null)
const timelineHandles = ref([])

const playoutTweens = []

onMounted(() => {
  const timeline = gsapTimeline({ name: 'Tiktok Feed' })
    .from(phoneElement, { duration: frames(75), scale: 0.75, transformOrigin: 'center bottom', ease: 'power4.inOut' })
    .add(timelineTitle, `=-${frames(30)}`)
    .from(
      socialMediaReferencesElement.value.querySelectorAll('img'),
      {
        duration: frames(4),
        scale: 0,
        transformOrigin: 'center',
        ease: 'power2.inOut',
        stagger: { amount: frames(4) },
      },
      `=-${frames(10)}`
    )
    .add(timelineHandles.value.reverse(), '<')

  timelineHandles.value.forEach((handleTimeline, i) => {
    timeline.add(handleTimeline, `=-${i * frames(6)}`) // Stagger handles by 6 frames
  })

  const paneElements = gsap.utils.toArray(panesElement.value.children).slice(1)
  paneElements.forEach((el, i) => {
    playoutTweens.push(
      gsap.from(el, {
        duration: frames(8),
        yPercent: 100,
        delay: frames(38) + i * 8, // Manual stagger of 8 seconds, with 38-frame offset for the first item
        onStart: () => {
          el.play()
          gsap.to(paneElements[i - 1], { duration: frames(8), yPercent: -100 })
        },
      })
    )
  })
})

onUnmounted(() => {
  playoutTweens.forEach((tl) => tl.kill())
})

const randomlyOrderedFeed = computed(() => {
  if (!item.enrichment.feed) return []

  return [...item.enrichment.feed].sort(() => 0.5 - Math.random())
})
</script>

<template>
  <ToFullTopLeft>
    <AnimatedBackground :lottie="PositiveWaveLoop1" :backgroundColor="scheme.backgroundColor" :scale="3" :color="scheme.color">
      <div class="pl-36 h-full flex flex-col justify-center w-[1050px] space-y-12">
        <CharacterJumpFromTheRight :text="item.properties.title" class="text-q-cr-red-primary font-bold" textSizeClass="text-[10rem]" @timeline="timelineTitle = $event" />
        <div ref="socialMediaReferencesElement" class="space-y-10">
          <div v-for="{ uuid, image, handle } in item.properties.socialMediaReferences" :key="uuid" class="flex items-center space-x-6">
            <img ref="referenceImageElements" :src="image" class="size-24 object-contain" />
            <CharacterEnlarge :text="handle" class="text-q-cr-gray font-bold text-[5rem]" @timeline="timelineHandles.push($event)" />
          </div>
        </div>
      </div>
      <div ref="phoneElement" class="absolute w-[531px] h-[1080px] left-[1150px] top-[100px]">
        <div class="absolute top-0 left-0 size-full p-5 pb-30">
          <div ref="panesElement" class="relative bg-green-500 size-full rounded-xl overflow-hidden">
            <div class="absolute size-full bg-white text-5xl flex flex-col items-center justify-center space-y-16">
              <img src="../../../assets/images/qmusic_logo.png" class="w-1/2" />
              <div class="flex items-center space-x-4">
                <TiktokIcon class="size-8 mb-1 fill-current" :fontControlled="false" />
                <span>{{ item.properties.tiktokUsername }}</span>
              </div>
            </div>
            <VideoPlayer v-for="video in randomlyOrderedFeed" :key="video.id" class="absolute size-full object-cover bg-q-cr-red-primary" :src="video.url" loop muted />
          </div>
        </div>
        <img src="../../../assets/images/phone_outline.png" class="absolute top-0 left-0 h-full drop-shadow-[0_25px_25px_rgb(0_0_0/_0.3)]" />
      </div>
    </AnimatedBackground>
  </ToFullTopLeft>
</template>
